const CHECK_INTERVAL_MS = 30_000;
const RELOAD_GUARD_MS = 60_000;
const RELOAD_KEY = 'kuutio-build-reload';
// What a backend answers a request the client should no longer be making:
// a moved route (404/410) or one a service stopped serving to browsers (401/403).
const REFUSED_STATUSES = new Set([401, 403, 404, 410]);

export const SOCKET_CONNECT_ERROR_EVENT = 'kuutio:socket-connect-error';

/**
 * Stale-build reload (#1211). A tab left open across a deploy keeps running
 * the old bundle, and the new backend refuses its requests: a screen then
 * shows empty data where it should show an error. The tab compares its own
 * build id (VITE_BUILD_ID, set in vite.config.js) with the deployed one in
 * /version.json and reloads when they differ.
 *
 * It checks when a request is refused or fails, when the socket cannot
 * connect, and when the tab becomes visible again; at most once per 30 s.
 * A refused request with the current build (an expired session, say) costs
 * one small fetch and nothing else.
 *
 * Inject `win` / `storage` / `now` for DOM-free tests.
 */
export function createStaleBuildWatcher({
  win = typeof window !== 'undefined' ? window : null,
  storage = (() => { try { return win?.sessionStorage ?? null; } catch { return null; } })(),
  ownBuildId,
  versionUrl,
  now = () => Date.now(),
} = {}) {
  const nativeFetch = win.fetch.bind(win);
  let lastCheck = -Infinity;
  let pending = null;

  function readGuard() {
    try { return Number(storage?.getItem(RELOAD_KEY)) || 0; } catch { return 0; }
  }

  function reload() {
    // A reload that still serves the old build (a lagging CDN edge) must not loop.
    if (now() - readGuard() < RELOAD_GUARD_MS) return false;
    try { storage?.setItem(RELOAD_KEY, String(now())); } catch { /* storage blocked */ }
    console.warn('[buildVersion] a newer build is deployed, reloading');
    win.location.reload();
    return true;
  }

  async function fetchDeployedId() {
    const res = await nativeFetch(`${versionUrl}?t=${now()}`, { cache: 'no-store' });
    if (!res.ok) return null;
    const body = await res.json();
    return typeof body?.buildId === 'string' ? body.buildId : null;
  }

  /** Resolves true when the tab is reloading onto a newer build. */
  function check() {
    if (pending) return pending;
    if (now() - lastCheck < CHECK_INTERVAL_MS) return Promise.resolve(false);
    lastCheck = now();
    pending = fetchDeployedId()
      .then((deployedId) => (deployedId && deployedId !== ownBuildId ? reload() : false))
      .catch(() => false) // offline or no version.json: nothing to compare
      .finally(() => { pending = null; });
    return pending;
  }

  function install() {
    win.fetch = async (...args) => {
      let res;
      try {
        res = await nativeFetch(...args);
      } catch (err) {
        if (err?.name !== 'AbortError') check();
        throw err;
      }
      if (REFUSED_STATUSES.has(res.status)) check();
      return res;
    };
    win.addEventListener(SOCKET_CONNECT_ERROR_EVENT, () => check());
    win.document?.addEventListener('visibilitychange', () => {
      if (win.document.visibilityState === 'visible') check();
    });
  }

  return { check, install };
}

/** Wires the watcher into this tab. A dev server has no version.json, so it is skipped there. */
export function installStaleBuildReload() {
  const ownBuildId = import.meta.env.VITE_BUILD_ID;
  if (import.meta.env.DEV || !ownBuildId) return;
  createStaleBuildWatcher({ ownBuildId, versionUrl: `${import.meta.env.BASE_URL}version.json` }).install();
}
